const { createWeekCache } = require("./weekCache.js");
const { fetchTodoListsRange, putDocument, deleteDocument, getDocument } = require("./engine.js");
const { weekDays, weekStart } = require("../mobile/dayNav.js");
const { loadMeta, saveMeta } = require("./cache.js");
const { applySnapshot, docKey } = require("../../shared/syncState.js");
const { ensureTodoIds } = require("./todoMerge.js");

/** Bumped on each applyWeek so late IDB reads do not stomp server data. */
let weekApplyEpoch = 0;

function getWeekApplyEpoch() {
  return weekApplyEpoch;
}

/** In-flight write-throughs — applyWeek waits so it does not refetch stale empties. */
let pendingWrites = 0;
const writeIdleWaiters = [];

function trackWrite(promise) {
  if (!promise || typeof promise.then !== "function") return promise;
  pendingWrites += 1;
  return promise.finally(() => {
    pendingWrites -= 1;
    if (pendingWrites <= 0) {
      pendingWrites = 0;
      const waiters = writeIdleWaiters.splice(0, writeIdleWaiters.length);
      waiters.forEach((resolve) => resolve());
    }
  });
}

function whenWritesIdle() {
  if (pendingWrites <= 0) return Promise.resolve();
  return new Promise((resolve) => writeIdleWaiters.push(resolve));
}

/**
 * Keep applyWeek blocked for the whole async batch (e.g. materialize COUNT days),
 * even between individual PUTs when pendingWrites would otherwise hit 0.
 */
async function withWriteHold(fn) {
  pendingWrites += 1;
  try {
    return await fn();
  } finally {
    pendingWrites -= 1;
    if (pendingWrites <= 0) {
      pendingWrites = 0;
      const waiters = writeIdleWaiters.splice(0, writeIdleWaiters.length);
      waiters.forEach((resolve) => resolve());
    }
  }
}

const LOCAL_KEYS = {
  config: "config",
  custom_list_ids: "customTodoListIds",
};

const DOC_STORES = {
  todo_list: "todo_lists",
  repeating_event: "repeating_events",
  repeating_event_by_date: "repeating_events_by_date",
};

const IDB_STORES = ["todo_lists", "repeating_events", "repeating_events_by_date"];

const weekCache = createWeekCache({ ttlMs: 60000 });

/** Revisions for non-week docs (repeating_event, by_date) kept in syncMeta.docs */
function getBaseRevision(docType, docId) {
  if (docType === "todo_list") {
    const rev = weekCache.getRevision(String(docId));
    if (typeof rev === "number") return rev;
  }
  const meta = loadMeta();
  const entry = meta.docs && meta.docs[docKey(docType, String(docId))];
  return entry && typeof entry.baseRevision === "number" ? entry.baseRevision : 0;
}

function setCleanRevision(docType, docId, revision) {
  const meta = loadMeta();
  if (!meta.docs) meta.docs = {};
  meta.docs[docKey(docType, String(docId))] = {
    baseRevision: typeof revision === "number" ? revision : 0,
    dirty: false,
  };
  // Drop legacy tombstones — server is authority
  if (meta.deletedTodos) delete meta.deletedTodos;
  saveMeta(meta);
}

function requireOnline() {
  const root =
    typeof window !== "undefined" ? window : typeof global !== "undefined" ? global : undefined;
  const nav = root && root.navigator;
  if (nav && nav.onLine === false) {
    const err = new Error("网络不可用");
    err.code = "OFFLINE";
    throw err;
  }
}

function publishSyncEvent(name, detail) {
  if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") return;
  window.dispatchEvent(new CustomEvent(name, { detail }));
}

function getWeekCache() {
  return weekCache;
}

function openDatabase() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open("weekToDo", 4);
    request.onupgradeneeded = function (event) {
      const db = event.target.result;
      for (const name of IDB_STORES) {
        if (!db.objectStoreNames.contains(name)) {
          db.createObjectStore(name, { autoIncrement: false });
        }
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function withStores(db, fn) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORES, "readwrite");
    fn(tx);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error("IndexedDB 事务中止"));
  });
}

/** IndexedDB structured-clone cannot store Vue reactive Proxies. */
function cloneForIdb(payload) {
  return JSON.parse(JSON.stringify(payload === undefined ? null : payload));
}

async function putIndexedDbRows(rows) {
  if (!rows.length) return;
  const db = await openDatabase();
  try {
    await withStores(db, (tx) => {
      for (const { table, docId, payload } of rows) {
        tx.objectStore(table).put(cloneForIdb(payload), docId);
      }
    });
  } finally {
    db.close();
  }
}

function mergeSyncMeta(documents) {
  const list = Array.isArray(documents) ? documents : [];
  if (!list.length) return;
  const next = applySnapshot(loadMeta(), list);
  if (next.deletedTodos) delete next.deletedTodos;
  saveMeta(next);
}

/**
 * Apply bootstrap documents — server payloads replace local (no dirty shield / merge).
 */
async function applyBootstrapDocuments(documents) {
  const list = Array.isArray(documents) ? documents : [];
  const idbRows = [];

  for (const document of list) {
    if (!document || !document.docType) continue;
    const { docType, docId, payload } = document;
    const localKey = LOCAL_KEYS[docType];
    if (localKey) {
      localStorage.setItem(localKey, JSON.stringify(payload));
      continue;
    }
    const table = DOC_STORES[docType];
    if (!table || docType === "todo_list") continue;
    idbRows.push({ table, docId, payload });
  }

  await putIndexedDbRows(idbRows);
  mergeSyncMeta(list.filter((doc) => doc.docType !== "todo_list"));
}

async function putDocAndCache(docType, docId, payload, opts = {}) {
  requireOnline();
  const id = String(docId);
  const baseRevision = getBaseRevision(docType, id);
  const plainPayload = cloneForIdb(payload);
  const result = await putDocument(docType, id, plainPayload, baseRevision);

  if (result.conflict) {
    const serverPayload = result.payload;
    if (docType === "todo_list") {
      const remote = Array.isArray(serverPayload) ? serverPayload : [];
      const local = Array.isArray(plainPayload) ? plainPayload : [];
      const { mergeTodoList } = require("./todoMerge.js");
      const merged = mergeTodoList(local, remote);
      setCleanRevision(docType, id, result.revision);
      weekCache.patchDay(id, {
        docId: id,
        payload: merged.list,
        revision: result.revision,
        updatedAt: result.updatedAt,
      });
      await putIndexedDbRows([{ table: DOC_STORES.todo_list, docId: id, payload: merged.list }]);
      publishSyncEvent("todo-list-server-replace", { listId: id, payload: merged.list });
      // Retry once so newly materialized instances are not dropped on stale baseRevision.
      // `merged` stays set when that retry commits: the caller's delete may have been restored.
      if (merged.needsPush && !opts._conflictRetried) {
        const retried = await putDocAndCache(docType, id, merged.list, { _conflictRetried: true });
        return retried && retried.merged ? retried : { ...retried, merged: true };
      }
      publishSyncEvent("sync-conflict", {
        conflicts: [{ docType, docId: id }],
      });
      return { ...result, payload: merged.list, conflict: true, merged: true };
    }
    const table = DOC_STORES[docType];
    if (table) await putIndexedDbRows([{ table, docId: id, payload: serverPayload }]);
    setCleanRevision(docType, id, result.revision);
    publishSyncEvent("sync-conflict", {
      conflicts: [{ docType, docId: id }],
    });
    return result;
  }

  if (docType === "todo_list") {
    const list = Array.isArray(plainPayload) ? plainPayload : [];
    weekCache.patchDay(id, {
      docId: id,
      payload: list,
      revision: result.revision,
      updatedAt: result.updatedAt,
    });
    await putIndexedDbRows([{ table: DOC_STORES.todo_list, docId: id, payload: list }]);
  } else {
    const table = DOC_STORES[docType];
    if (table) await putIndexedDbRows([{ table, docId: id, payload: plainPayload }]);
  }
  setCleanRevision(docType, id, result.revision);
  return result;
}

async function saveTodoList(docId, payload) {
  const list = Array.isArray(payload) ? payload : [];
  return trackWrite(putDocAndCache("todo_list", docId, list));
}

async function saveRepeatingEvent(docId, payload) {
  return trackWrite(putDocAndCache("repeating_event", docId, payload));
}

async function deleteRepeatingEvent(docId) {
  return trackWrite(
    (async () => {
      requireOnline();
      const id = String(docId);
      let baseRevision = getBaseRevision("repeating_event", id);
      let result = await deleteDocument("repeating_event", id, baseRevision);
      // Stale local revision: retry once with the server's current revision
      if (result.conflict && typeof result.revision === "number") {
        result = await deleteDocument("repeating_event", id, result.revision);
      }
      if (result.conflict) {
        setCleanRevision("repeating_event", id, result.revision);
        publishSyncEvent("sync-conflict", {
          conflicts: [{ docType: "repeating_event", docId: id }],
        });
        return result;
      }
      const db = await openDatabase();
      try {
        await withStores(db, (tx) => {
          tx.objectStore(DOC_STORES.repeating_event).delete(id);
        });
      } finally {
        db.close();
      }
      const meta = loadMeta();
      if (meta.docs) delete meta.docs[docKey("repeating_event", id)];
      saveMeta(meta);
      return result;
    })()
  );
}

async function saveRepeatingEventByDate(docId, payload) {
  const map = payload && typeof payload === "object" ? payload : {};
  return trackWrite(putDocAndCache("repeating_event_by_date", docId, map));
}

async function ensureCustomListLoaded(vuexStore, listId) {
  const id = String(listId);
  if (weekCache.getRevision(id) !== undefined) {
    const cached = weekCache.getDay(id);
    let payload = Array.isArray(cached.payload) ? cached.payload : [];
    const ensured = ensureTodoIds(payload);
    payload = ensured.list;
    vuexStore.commit("loadTodoLists", { todoListId: id, todoList: payload });
    if (ensured.changed) {
      try {
        await saveTodoList(id, payload);
      } catch (_) {
        /* offline: show healed ids in memory only */
      }
    }
    return payload;
  }

  const doc = await getDocument("todo_list", id);
  const remote = doc && Array.isArray(doc.payload) ? doc.payload : [];
  const ensured = ensureTodoIds(remote);
  const payload = ensured.list;
  const revision = doc && typeof doc.revision === "number" ? doc.revision : 0;
  weekCache.patchDay(id, {
    docId: id,
    payload,
    revision,
    updatedAt: doc?.updatedAt,
  });
  vuexStore.commit("loadTodoLists", { todoListId: id, todoList: payload });
  await putIndexedDbRows([{ table: DOC_STORES.todo_list, docId: id, payload }]);
  setCleanRevision("todo_list", id, revision);
  if (ensured.changed) {
    try {
      await saveTodoList(id, payload);
    } catch (_) {
      /* ignore */
    }
  }
  return payload;
}

/**
 * Pull visible days from server and replace local — no merge.
 * @param {object} [options]
 * @param {string[]} [options.days] visible day ids (sliding window may span 2 weeks)
 * @param {boolean} [options.force=true] always refetch (server is authority on navigate)
 */
async function applyWeekToStore(vuexStore, dayId, weekStartsOnMonday = true, options = {}) {
  const force = options.force !== false;
  const days = Array.isArray(options.days) && options.days.length
    ? options.days.map(String)
    : weekDays(dayId, weekStartsOnMonday);
  const weekStarts = [...new Set(days.map((id) => weekStart(id, weekStartsOnMonday)))];
  const epoch = ++weekApplyEpoch;

  // Let in-flight materialize / edits land on the server before refetching
  await whenWritesIdle();

  try {
    for (const start of weekStarts) {
      await weekCache.ensureWeek({
        dayId: start,
        weekStartsOnMonday,
        force,
        fetch: (from, to) => fetchTodoListsRange(from, to),
      });
    }
  } catch (error) {
    const anyCached = weekStarts.some((start) => weekCache.hasWeek(start, weekStartsOnMonday));
    if (!anyCached) throw error;
  }

  if (epoch !== weekApplyEpoch) return;

  const idbRows = [];
  const syncDocs = [];

  for (const id of days) {
    const doc = weekCache.getDay(id);
    const remote = Array.isArray(doc.payload) ? doc.payload : [];
    const ensured = ensureTodoIds(remote);
    const payload = ensured.list;

    weekCache.patchDay(id, {
      docId: id,
      payload,
      revision: doc.revision,
      updatedAt: doc.updatedAt,
    });
    vuexStore.commit("loadTodoLists", { todoListId: id, todoList: payload });
    idbRows.push({ table: DOC_STORES.todo_list, docId: id, payload });
    if (typeof doc.revision === "number") {
      syncDocs.push({
        docType: "todo_list",
        docId: id,
        revision: doc.revision,
        payload,
      });
    }
    if (ensured.changed) {
      // Backfill ids on server when online; do not mark dirty-queue.
      try {
        await saveTodoList(id, payload);
      } catch (_) {
        /* offline: memory/IDB have healed ids until next online write */
      }
    }
  }

  await putIndexedDbRows(idbRows);
  if (epoch !== weekApplyEpoch) return;
  mergeSyncMeta(syncDocs);
}

/**
 * Align weekCache revision with server, keeping any in-memory todos (merge by id).
 * Used before materialize so off-screen days do not PUT with a stale baseRevision.
 */
async function hydrateTodoListDay(vuexStore, listId) {
  const id = String(listId);
  const doc = await getDocument("todo_list", id);
  const remote = doc && Array.isArray(doc.payload) ? doc.payload : [];
  const local =
    vuexStore && Array.isArray(vuexStore.getters.todoLists[id])
      ? vuexStore.getters.todoLists[id]
      : [];
  const { mergeTodoList } = require("./todoMerge.js");
  const merged = mergeTodoList(local, remote);
  const payload = merged.list;
  const revision = doc && typeof doc.revision === "number" ? doc.revision : 0;
  weekCache.patchDay(id, {
    docId: id,
    payload,
    revision,
    updatedAt: doc?.updatedAt,
  });
  setCleanRevision("todo_list", id, revision);
  await putIndexedDbRows([{ table: DOC_STORES.todo_list, docId: id, payload }]);
  if (vuexStore) {
    vuexStore.commit("loadTodoLists", { todoListId: id, todoList: payload });
  }
  return payload;
}

/** Reload one day from server into Vuex (after failed local write). */
async function reloadTodoListFromServer(vuexStore, listId) {
  const id = String(listId);
  try {
    const doc = await getDocument("todo_list", id);
    const payload = doc && Array.isArray(doc.payload) ? doc.payload : [];
    const revision = doc && typeof doc.revision === "number" ? doc.revision : 0;
    weekCache.patchDay(id, {
      docId: id,
      payload,
      revision,
      updatedAt: doc?.updatedAt,
    });
    await putIndexedDbRows([{ table: DOC_STORES.todo_list, docId: id, payload }]);
    setCleanRevision("todo_list", id, revision);
    if (vuexStore) {
      vuexStore.commit("loadTodoLists", { todoListId: id, todoList: payload });
    } else {
      publishSyncEvent("todo-list-server-replace", { listId: id, payload });
    }
    return payload;
  } catch (error) {
    publishSyncEvent("sync-write-failed", {
      listId: id,
      message: error && error.message ? error.message : "同步失败",
    });
    throw error;
  }
}

module.exports = {
  weekCache,
  getWeekCache,
  getWeekApplyEpoch,
  requireOnline,
  applyBootstrapDocuments,
  applyWeekToStore,
  ensureCustomListLoaded,
  saveTodoList,
  saveRepeatingEvent,
  deleteRepeatingEvent,
  saveRepeatingEventByDate,
  reloadTodoListFromServer,
  hydrateTodoListDay,
  putIndexedDbRows,
  withWriteHold,
  DOC_STORES,
};
